import { clearCachePrefix } from './sessionCache';

/**
 * 고치고 나면 옛 값을 버린다 — 저장 요청 하나가 나가면 그 값을 담고 있던 보관함을 비운다.
 *
 * 왜 필요한가 (2026-08-12 대표님: 「마감일 기재가 완료된 것 같은데 왜 안 되지」):
 * 캠페인 상세에서 업로드 마감을 적고 목록으로 돌아가면 대시보드 카드가
 * **「일정 미입력 1건」을 그대로 보여 줬다.** 서버에는 이미 들어가 있었다 —
 * 목록 응답이 `sessionStorage` 에 3분간 남아 있어서 «적기 전 응답»이 다시 나온 것이다.
 * 보관함을 비우는 함수(`clearCampaignsByMonth` 등)는 만들어 두고 **아무도 부르지 않았다.**
 *
 * 부르는 자리를 화면마다 두면 또 빠뜨린다(저장하는 자리가 수십 군데다).
 * 그래서 **요청이 나가는 길목 한 곳**(axios 응답 인터셉터)에서 처리한다.
 *
 * ⚠ 정산 집계(`settlement:base:`)는 **일부러 비우지 않는다.** 그건 캠페인 1건당 조회 1번이라
 *   한 번 비우면 정산 화면이 처음부터 다시 몇십 번을 긁는다. 캠페인을 저장할 때마다 그러면
 *   「정산이 느리다」가 되돌아온다. 그쪽은 정산 화면의 새로고침 버튼이 이미 담당한다.
 */

/** 보관함 이름표 — 담는 쪽과 비우는 쪽이 같은 문자열을 봐야 한다 */
export const CACHE_PREFIX = {
  /** 월별 캠페인 목록 (캠페인 대시보드 카드) — 조회 1번짜리라 가볍게 다시 받는다 */
  campaignsByMonth: 'campaigns:month:',
  /** 캠페인별 확정 청구액 (이번 달 청구표) — 금액을 고치면 바로 틀려진다 */
  billedByCampaign: 'settle:billed:',
  /** 배송 목록 (배송 화면·배송 손익) */
  shippingList: 'shipping:list:',
};

/** 이 주소에 무언가를 쓰면 어느 보관함이 옛 값이 되는가 */
const RULES = [
  { match: '/api/campaigns', clears: [CACHE_PREFIX.campaignsByMonth, CACHE_PREFIX.billedByCampaign] },
  { match: '/api/settlements', clears: [CACHE_PREFIX.billedByCampaign] },
  { match: '/api/shipping', clears: [CACHE_PREFIX.shippingList] },
  { match: '/api/deliveries', clears: [CACHE_PREFIX.shippingList] },
];

/** 읽기만 하는 요청은 아무것도 버리지 않는다 */
const WRITE_METHODS = ['post', 'put', 'patch', 'delete'];

export const isWriteMethod = (method) => WRITE_METHODS.includes(String(method || '').toLowerCase());

/**
 * 저장이 성공한 뒤 부른다. 지운 보관함 이름을 돌려준다(시험·디버깅용).
 * @param {string} url  요청 주소
 * @param {string} method  http 메서드
 * @returns {string[]} 비운 보관함 접두어
 */
export const invalidateCachesFor = (url, method = 'post') => {
  if (!isWriteMethod(method)) return [];

  const path = String(url || '');
  const cleared = [...new Set(RULES.filter((rule) => path.includes(rule.match)).flatMap((rule) => rule.clears))];
  cleared.forEach((prefix) => clearCachePrefix(prefix));
  return cleared;
};

export default invalidateCachesFor;
